import { useState, type FC } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { supabase } from "@/shared/lib/supabase";
import { LAWYER_ROUTES } from "@/shared/constants/paths";
import { sentryUtils } from "@/shared/utils/sentry";
import { useLawyerSignup } from "./LawyerSignupContext";
import { saveLawyerSignupSource, type LawyerSignupFlow } from "./signupSource";

import { AttorneyAuthLayout } from "./components/AttorneyAuthLayout";
import { IntroStep } from "./components/IntroStep";
import { CreateAccountStep } from "./components/CreateAccountStep";

function getFlowType(searchParams: URLSearchParams): LawyerSignupFlow {
  if (searchParams.has("token")) return "invited";
  if (searchParams.get("ref") === "email_outreach") return "cold-email";
  return "organic";
}

const LawyerSignupPage: FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setCredentials } = useLawyerSignup();

  const flowType = getFlowType(searchParams);
  saveLawyerSignupSource(flowType);
  const inviteToken = searchParams.get("token") ?? undefined;
  const inviterName = searchParams.get("inviter") ?? "";
  const inviteEmail = searchParams.get("email") ?? undefined;

  const showIntro = flowType !== "organic";
  const [showCreateAccount, setShowCreateAccount] = useState(!showIntro);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleIntroNext = () => {
    setShowCreateAccount(true);
  };

  const handleCreateAccount = async (email: string, password: string) => {
    setLoading(true);
    setError(null);

    try {
      const { data, error: invokeError } = await supabase.functions.invoke(
        "communications/verification-code/send",
        { body: { email, password } },
      );

      if (invokeError) {
        setError("Failed to send verification code. Please try again.");
        return;
      }

      if (data?.error) {
        setError(data.error as string);
        return;
      }

      setCredentials(email, password);
      sessionStorage.setItem("lawyer_signup_email", email);
      if (inviteToken) {
        sessionStorage.setItem("lawyer_invite_token", inviteToken);
      }
      navigate(LAWYER_ROUTES.SIGNUP_VERIFY);
    } catch (err) {
      const message = err instanceof Error ? err.message : "An unexpected error occurred";
      setError(message);
      sentryUtils.captureError(err, "Attorney signup: send verification code failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AttorneyAuthLayout showSettlementBar={flowType !== "invited"}>
      {!showCreateAccount && (
        <IntroStep
          onContinue={handleIntroNext}
          inviterName={flowType === "invited" ? inviterName : undefined}
        />
      )}

      {showCreateAccount && (
        <CreateAccountStep
          prefillEmail={flowType === "invited" ? inviteEmail : undefined}
          inviteToken={inviteToken}
          onSubmit={handleCreateAccount}
          isLoading={loading}
          error={error}
          onClearError={() => setError(null)}
          onError={setError}
        />
      )}
    </AttorneyAuthLayout>
  );
};

export default LawyerSignupPage;
